import { useState, type ReactNode } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { NavLink, useLocation } from 'react-router-dom';

import { useIdentity } from '@/auth/AuthGate';
import { signOut } from '@/auth/session';
import { EnvironmentSwitcher } from '@/environment/EnvironmentSwitcher';
import { FEATURES } from '@/features/flags';
import { useIsMobile } from '@/hooks/useIsMobile';
import { APP_BAR_HEIGHT, DRAWER_WIDTH, SEP_GREEN_PALE } from '@/theme';

interface NavEntry {
  label: string;
  to: string;
  icon: ReactNode;
  disabled?: boolean;
  disabledHint?: string;
}

const NAV: NavEntry[] = [
  { label: 'Lizenzvergabe', to: '/', icon: <LockOpenOutlinedIcon /> },
  { label: 'Mandanten', to: '/tenants', icon: <BusinessOutlinedIcon /> },
  { label: 'Benutzer', to: '/users', icon: <PeopleOutlinedIcon /> },
  { label: 'Apps', to: '/apps', icon: <AppsOutlinedIcon /> },
];

const SECONDARY_NAV: NavEntry[] = [
  { label: 'Protokoll', to: '/audit', icon: <HistoryOutlinedIcon /> },
];

export function AppShell({ children }: { children: ReactNode }) {
  const identity = useIdentity();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Navigating on a phone dismisses the drawer — otherwise it covers the page
  // the user just asked for. Done on the click rather than in an effect
  // watching the path: the drawer closing IS the click's consequence, and an
  // effect would re-run on every navigation including ones that never touched
  // the drawer.
  const closeDrawer = () => setDrawerOpen(false);

  const renderNav = (entries: NavEntry[]) => (
    <List sx={{ py: 0 }}>
      {entries.map((entry) => {
        const selected =
          entry.to === '/' ? location.pathname === '/' : location.pathname.startsWith(entry.to);
        const item = (
          <ListItemButton
            key={entry.label}
            component={entry.disabled ? 'div' : NavLink}
            to={entry.disabled ? undefined : entry.to}
            disabled={entry.disabled}
            selected={selected}
            onClick={closeDrawer}
            sx={{
              // 48px is the comfortable minimum for a thumb; the desktop default
              // is smaller than that.
              minHeight: 48,
              '&.Mui-selected': {
                backgroundColor: SEP_GREEN_PALE,
                '&:hover': { backgroundColor: SEP_GREEN_PALE },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: selected ? 'primary.main' : undefined }}>
              {entry.icon}
            </ListItemIcon>
            {/* v9 removed system shorthand props from Typography, so the size
                goes through `sx` rather than a bare `fontSize` prop. */}
            <ListItemText
              primary={entry.label}
              slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
            />
            {entry.disabled && <Chip label="bald" variant="outlined" />}
          </ListItemButton>
        );
        return entry.disabled && entry.disabledHint ? (
          <Tooltip key={entry.label} title={entry.disabledHint} placement="right">
            {/* A disabled control swallows pointer events, so the tooltip needs
                its own wrapper to have something to listen on. */}
            <span>{item}</span>
          </Tooltip>
        ) : (
          item
        );
      })}
    </List>
  );

  const navigation = (
    <Box sx={{ overflow: 'auto' }}>
      {renderNav(NAV)}
      <Divider />
      {renderNav([
        {
          label: 'Onboarding',
          to: '/onboarding',
          icon: <RocketLaunchOutlinedIcon />,
          // An administrator tool for creating tenants — deliberately not an
          // app in the catalogue, so it never appears in the license matrix.
          // Present but switched off (doc 11), shown rather than hidden so
          // staff can see what is coming instead of asking for it.
          disabled: !FEATURES.onboarding,
          disabledHint: 'Assistent zum Anlegen neuer Mandanten. Wird derzeit entwickelt.',
        },
      ])}
      <Divider />
      {renderNav(SECONDARY_NAV)}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar variant="dense" sx={{ minHeight: APP_BAR_HEIGHT, px: { xs: 1, sm: 2 }, gap: 1 }}>
          {isMobile && (
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              aria-label="Navigation öffnen"
              sx={{ color: '#fff' }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* The official SEP logo, on a white plate. It is blue type with a
              green pin on a transparent ground, so on the green app bar it
              would all but disappear — a plate keeps the brand colours instead
              of recolouring the logo to white.

              The wordmark already reads "SEP", so the title beside it is just
              "Admin"; repeating it would read "SEP SEP Admin". */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#fff',
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
            }}
          >
            <Box
              component="img"
              src="/img/sep-logo.svg"
              alt="Swiss Energy Planning"
              sx={{ height: 20, width: 'auto', display: 'block' }}
            />
          </Box>
          <Typography
            variant="subtitle1"
            component="div"
            sx={{
              color: '#fff',
              whiteSpace: 'nowrap',
              // Hidden on a phone: the environment matters more than the word
              // "Admin" when there is room for only one of them.
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Admin
          </Typography>
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', ml: 1 }}>
            <EnvironmentSwitcher />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              mr: 1,
              // An e-mail address eats the whole bar on a phone; the avatar menu
              // still identifies the user.
              display: { xs: 'none', md: 'block' },
            }}
          >
            {identity.user}
          </Typography>
          <IconButton
            size="small"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            aria-label="Benutzermenü"
          >
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 13 }}>
              {identity.user.slice(0, 1).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <Box sx={{ px: 2, py: 1, display: { xs: 'block', md: 'none' } }}>
              <Typography variant="caption" color="text.secondary">
                {identity.user}
              </Typography>
            </Box>
            <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
            <MenuItem
              component={NavLink}
              to="/settings/credentials"
              onClick={() => setMenuAnchor(null)}
            >
              <ListItemIcon>
                <KeyOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Service-Tokens
            </MenuItem>
            {/* Everything this UI does is available over the API, so the spec is
                worth one click. A plain <a>, not a router link: /docs is served
                by FastAPI, not by the SPA. */}
            <MenuItem
              component="a"
              href="/docs"
              target="_blank"
              rel="noreferrer"
              onClick={() => setMenuAnchor(null)}
            >
              <ListItemIcon>
                <ApiOutlinedIcon fontSize="small" />
              </ListItemIcon>
              API-Dokumentation
            </MenuItem>
            <Divider />
            <MenuItem onClick={signOut}>
              <ListItemIcon>
                <LogoutOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Abmelden
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Temporary drawer on a phone, permanent from `md` up. `keepMounted`
          because opening the menu should be instant on a touch device. */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: { xs: 0, md: DRAWER_WIDTH },
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: APP_BAR_HEIGHT }} />
        {navigation}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, bgcolor: 'background.default' }}>
        <Toolbar variant="dense" sx={{ minHeight: APP_BAR_HEIGHT }} />
        <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
}
